import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Error Boundary to catch DOM manipulation errors from browser extensions
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's the removeChild error from browser extensions
    if (error.message && error.message.includes('removeChild')) {
      // Ignore this error and don't update state
      return null;
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error but don't crash for DOM manipulation errors
    if (error.message && error.message.includes('removeChild')) {
      console.warn('Browser extension DOM conflict detected, ignoring...');
      return;
    }
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Une erreur est survenue</h1>
            <p className="text-slate-600 mb-4">Veuillez rafraîchir la page</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600"
            >
              Rafraîchir
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TrackingPage from "./pages/TrackingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";

// Dashboard Components
import DashboardLayout from "./components/DashboardLayout";
import SenderDashboard from "./pages/dashboard/SenderDashboard";
import CarrierDashboard from "./pages/dashboard/CarrierDashboard";
import NewParcelPage from "./pages/dashboard/NewParcelPage";
import ParcelsListPage from "./pages/dashboard/ParcelsListPage";
import ParcelDetailPage from "./pages/dashboard/ParcelDetailPage";
import AvailableMissionsPage from "./pages/dashboard/AvailableMissionsPage";
import MyMissionsPage from "./pages/dashboard/MyMissionsPage";
import PaymentsPage from "./pages/dashboard/PaymentsPage";
import ProfilePage from "./pages/dashboard/ProfilePage";

// Trip Pages (Trajets)
import NewTripPage from "./pages/dashboard/NewTripPage";
import MyTripsPage from "./pages/dashboard/MyTripsPage";
import SearchTripsPage from "./pages/dashboard/SearchTripsPage";

// Identity Verification
import IdentityVerificationPage from "./pages/dashboard/IdentityVerificationPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminParcelsPage from "./pages/admin/AdminParcelsPage";
import AdminPayoutsPage from "./pages/admin/AdminPayoutsPage";
import AdminFinancialReport from "./pages/admin/AdminFinancialReport";

// Carrier Earnings Page
import CarrierEarningsPage from "./pages/dashboard/CarrierEarningsPage";
import CarrierHistoryPage from "./pages/dashboard/CarrierHistoryPage";

// User Dashboard (unified)
import UserDashboard from "./pages/dashboard/UserDashboard";

// Legal Pages
import CGUPage from "./pages/CGUPage";
import PrivacyPage from "./pages/PrivacyPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Dashboard Router - Routes based on user role
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  if (user?.role === 'carrier') {
    return <CarrierDashboard />;
  }
  
  // Les utilisateurs 'user' et 'sender' utilisent le SenderDashboard
  // (Le UserDashboard sera activé après tests)
  return <SenderDashboard />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/track/:id" element={<TrackingPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/cgu" element={<CGUPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['sender', 'carrier']}>
          <DashboardLayout>
            <DashboardRouter />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/new-parcel" element={
        <ProtectedRoute allowedRoles={['sender']}>
          <DashboardLayout>
            <NewParcelPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/parcels" element={
        <ProtectedRoute allowedRoles={['sender']}>
          <DashboardLayout>
            <ParcelsListPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/search-trips" element={
        <ProtectedRoute allowedRoles={['sender']}>
          <DashboardLayout>
            <SearchTripsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/parcel/:id" element={
        <ProtectedRoute allowedRoles={['sender', 'carrier']}>
          <DashboardLayout>
            <ParcelDetailPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/new-trip" element={
        <ProtectedRoute allowedRoles={['carrier']}>
          <DashboardLayout>
            <NewTripPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/my-trips" element={
        <ProtectedRoute allowedRoles={['carrier']}>
          <DashboardLayout>
            <MyTripsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/available" element={
        <ProtectedRoute allowedRoles={['carrier']}>
          <DashboardLayout>
            <AvailableMissionsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/my-missions" element={
        <ProtectedRoute allowedRoles={['carrier']}>
          <DashboardLayout>
            <MyMissionsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/payments" element={
        <ProtectedRoute allowedRoles={['sender']}>
          <DashboardLayout>
            <PaymentsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/earnings" element={
        <ProtectedRoute allowedRoles={['carrier']}>
          <DashboardLayout>
            <CarrierEarningsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/dashboard/history" element={
        <ProtectedRoute allowedRoles={['carrier']}>
          <DashboardLayout>
            <CarrierHistoryPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/profile" element={
        <ProtectedRoute allowedRoles={['sender', 'carrier']}>
          <DashboardLayout>
            <ProfilePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard/verification" element={
        <ProtectedRoute allowedRoles={['sender', 'carrier']}>
          <DashboardLayout>
            <IdentityVerificationPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <AdminDashboard />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <AdminUsersPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/parcels" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <AdminParcelsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/payouts" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <AdminPayoutsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <AdminFinancialReport />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout>
            <ProfilePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
